import React from "react";
import { Formik } from "formik";
import { compose } from "redux";
import { connect } from "react-redux";
import _, { times } from "lodash";
import {
  NONE,
  YIELD_MAINTENANCE,
  MAKE_WHOLE,
  FLAT_PERCENTAGE,
  DEFEASANCE,
  LOCKOUT,
  YIELD_MAINTENANCE_AGENCY,
  AGGREGATE_INTEREST,
  SPREAD_MAINTENANCE
} from "../../constants/prepaymentTypes";
import { transformRate, transformDscr, transformDecimal } from "../../utils/formTransformers";
import interestRateTypes from "../../constants/interestRateTypes";
import SimpleConfirmDialog from "../Global/ConfirmationDialogs/SimpleConfirmDialog";
import { updateLoanDetails } from "../../store/state/loan/loanDetails";

import {
  LoanSection,
  RecourseSection,
  QuoteHistorySection,
  CovenantsSection,
  HedgeRequirementsSection,
  OtherSection,
  PassedQuoteSection
} from "./LoanDetailsFormSections";
import LinkedHedgesSection from "./LoanDetailsFormSections/LinkedHedgesSection/LinkedHedgesSection";

class  LoanDetailsForm extends React.Component {
  constructor(props) {
    super(props);
    this.formik = React.createRef();
    this.state = {
      cashflowOverrideResetModalOpen: false,
      cashflowOverrideModalShouldUseSaveAndReturn: false,
      callbackToCall: null,
      initialValues: LoanDetailsForm.getDefaults()
    };
  }

  componentDidMount() {
    this.props.setRevert(this.formik.current.handleReset);
    this.props.setSave(this.save);
    this.props.setSaveAndReturn(this.saveAndReturn);
  }

  overrideNull(objValue, srcValue) {
    if (objValue === null) {
      return srcValue;
    }
    return objValue;
  }

  componentDidUpdate(prevProps) {
    if (this.props.loan !== prevProps.loan) {
      const newInitialValues = _.cloneDeep(this.props.loan);
      _.mergeWith(newInitialValues, LoanDetailsForm.getDefaults(), this.overrideNull);
      _.mergeWith(newInitialValues.details, LoanDetailsForm.getDefaults().details, this.overrideNull);
      _.mergeWith(newInitialValues.additionalAdvances, LoanDetailsForm.getDefaults().additionalAdvances, this.overrideNull);
      _.mergeWith(newInitialValues.extensionTerms, LoanDetailsForm.getDefaults().extensionTerms, this.overrideNull);
      this.setState({ initialValues: newInitialValues });
    }
  }

  save = ({ ignoreCashflowOverride }) => {
    if (!ignoreCashflowOverride && this.shouldShowOverrideConfirmation()) {
      this.setState({ cashflowOverrideResetModalOpen: true });
      return null;
    }
    const lenderValidation = this.doesPassMultipleLenderValidation();
    const propertyAllocationValidation = this.doesPassPropertyAllocationValidation();
    let amountDrawnValidation = this.doesPassDrawnValidation();
    if (lenderValidation.result && propertyAllocationValidation.result && amountDrawnValidation.result) {
      this.setState(
        {
          callbackToCall: this.props.updateCallback
        },
        () => {
          this.formik.current.handleSubmit();
        }
      );
    } else if (!lenderValidation.result) {
      this.props.openLenderValidationModal(lenderValidation);
    } else if (!propertyAllocationValidation.result){
      this.props.openPropertyValidationModal(propertyAllocationValidation);
    } else {
      this.props.openAmountDrawnValidationModal(amountDrawnValidation);
    }
  };

  shouldShowOverrideConfirmation = () => {
    if (!this.props.loan.details.cashflowOverrideTypeId) return false;
    const originalDetails = this.formik.current.props.initialValues.details;
    const originalAmort = this.formik.current.props.initialValues.amortizationDetails;
    const { details, amortizationDetails } = this.formik.current.state.values;

    if (
      this.valuesAreEffectivelyTheSame(originalDetails.loanTermMonths, details.loanTermMonths) &&
      this.valuesAreEffectivelyTheSame(originalDetails.maturityDate, details.maturityDate) &&
      this.valuesAreEffectivelyTheSame(originalDetails.daycountId, details.daycountId) &&
      this.valuesAreEffectivelyTheSame(originalDetails.rateTypeId, details.rateTypeId) &&
      this.valuesAreEffectivelyTheSame(originalDetails.indexId, details.indexId) &&
      this.valuesAreEffectivelyTheSame(originalDetails.indexFloor, details.indexFloor) &&
      this.valuesAreEffectivelyTheSame(originalDetails.loanSpread, details.loanSpread) &&
      this.valuesAreEffectivelyTheSame(originalDetails.closingDate, details.closingDate) &&
      this.valuesAreEffectivelyTheSame(originalDetails.paymentDay, details.paymentDay) &&
      this.valuesAreEffectivelyTheSame(originalDetails.businessDayAdjustmentId, details.businessDayAdjustmentId) &&
      this.valuesAreEffectivelyTheSame(originalDetails.roundingId, details.roundingId) &&
      this.valuesAreEffectivelyTheSame(originalDetails.embeddedCap, details.embeddedCap) &&
      this.valuesAreEffectivelyTheSame(originalDetails.lookbackDays, details.lookbackDays) &&
      this.valuesAreEffectivelyTheSame(originalDetails.amount, details.amount) &&
      this.valuesAreEffectivelyTheSame(originalAmort.ioPeriod, amortizationDetails.ioPeriod) &&
      this.valuesAreEffectivelyTheSame(originalAmort.amortizationTypeId, amortizationDetails.amortizationTypeId) &&
      this.valuesAreEffectivelyTheSame(originalAmort.amortizationTerm, amortizationDetails.amortizationTerm) &&
      this.valuesAreEffectivelyTheSame(originalAmort.amortizationStartDate, amortizationDetails.amortizationStartDate) &&
      this.valuesAreEffectivelyTheSame(originalAmort.amortizationRate, amortizationDetails.amortizationRate) &&
      this.valuesAreEffectivelyTheSame(originalAmort.monthlyPrincipalReduction, amortizationDetails.monthlyPrincipalReduction)
    ) {
      return false;
    }
    return true;
  };

  valuesAreEffectivelyTheSame(v1, v2) {
    if (v1 === null) v1 = "";
    if (v2 === null) v2 = "";
    return v1 == v2;
  }

  doesPassMultipleLenderValidation = () => {
    const { details } = this.formik.current.state.values;
    const { loanProvider } = details;
    let allocationSumCheck = false;
    let leadCheck = false;
    let nonNullCheck = true;

    if (!loanProvider) return true;

    // if just one lender, pass validation (nothing to validate)
    if (loanProvider.length === 1) {
      return { result: true };
    }

    if (loanProvider.length > 1) {
      const { amount } = details;
      // sum all lender allocation fields
      const lenderAllocationSum = loanProvider.reduce((accumulator, obj) => accumulator + Number(obj.loanCompanyBridge.lenderAllocation), 0);
      if (lenderAllocationSum === Number(amount)) {
        allocationSumCheck = true;
      }

      if (loanProvider.some(obj => obj.loanCompanyBridge.isLead === true)) {
        leadCheck = true;
      }

      loanProvider.forEach(lender => {
        if (!lender.id || !lender.loanCompanyBridge.lenderAllocation) {
          nonNullCheck = false;
        }
      });
    }
    return { result: allocationSumCheck && leadCheck && nonNullCheck, details: { allocationSumCheck, leadCheck, nonNullCheck } };
  };

  doesPassPropertyAllocationValidation = () => {

    const { properties } = this.props.loan;

    const allocationSumCheck = properties ? _.sumBy(properties, property => property.allocationPercent) === 1 || properties.length === 0 : 1;
    let uniquePropertyCheck = true;

    if (properties && properties.length > 0) {
      for (let propertyIndex = 0; propertyIndex < properties.length && uniquePropertyCheck; propertyIndex++) {
        uniquePropertyCheck = properties.filter(property => property.propertyId === properties[propertyIndex].propertyId).length === 1;
      }
    }
    return { result: allocationSumCheck && uniquePropertyCheck, details: { allocationSumCheck, uniquePropertyCheck } };
  };

  doesPassDrawnValidation = () => {
    
    let goodNewsCheck = true;
    let capExCheck = true;
    let fieldValues = this.formik.current.state.values; 

    let goodNewsStartingAmount = parseFloat(_.get(fieldValues, "covenants.tilcReserves"));
    let goodNewsAmountDrawn = parseFloat(_.get(fieldValues, "covenants.goodNewsMoneyDrawn"));
    if(goodNewsStartingAmount && goodNewsAmountDrawn && goodNewsAmountDrawn > goodNewsStartingAmount) {
      goodNewsCheck = false;
    }

    let capExStartingAmount = parseFloat(_.get(fieldValues, "covenants.capExReserves"));
    let capExAmountDrawn = parseFloat(_.get(fieldValues, "covenants.capExReservesDrawn"));
    if(capExStartingAmount && capExAmountDrawn && capExAmountDrawn > capExStartingAmount) {
      capExCheck = false;
    }

    return {
      result: goodNewsCheck && capExCheck,
      details: {
        goodNewsCheck,
        capExCheck,
      }
    }
  }

  saveAndReturn = ({ ignoreCashflowOverride }) => {
    if (!ignoreCashflowOverride && this.shouldShowOverrideConfirmation()) {
      this.setState({ cashflowOverrideResetModalOpen: true, cashflowOverrideModalShouldUseSaveAndReturn: true });
      return null;
    }
    const lenderValidation = this.doesPassMultipleLenderValidation();
    const propertyAllocationValidation = this.doesPassPropertyAllocationValidation();
    let amountDrawnValidation = this.doesPassDrawnValidation();
    if (lenderValidation.result && propertyAllocationValidation.result && amountDrawnValidation.result) {
      this.setState(
        {
          callbackToCall: this.props.saveAndReturnCallback
        },
        () => {
          this.formik.current.handleSubmit();
        }
      );
    } else if (!lenderValidation.result) {
      this.props.openLenderValidationModal(lenderValidation);
    } else if(!propertyAllocationValidation) {
      this.props.openPropertyValidationModal(propertyAllocationValidation);
    } else {
      this.props.openAmountDrawnValidationModal(amountDrawnValidation); 
    }
  };

  static getDefaults = () => {
    return {
      details: {
        name: "",
        lenderCompanyId: "",
        loanTypeId: "",
        conversionOption: "",
        loanStatusId: "",
        loanSeniorityId: "",
        counterparty: "",
        amount: "",
        initialProceeds: "",
        collateralization: "",
        proceedsDrawn: "",
        proceedsDrawnAsOf: "",
        closingDate: null,
        isClosingDateAssumed: false,
        lastModifiedDate: "",
        loanTermMonths: "",
        maturityDate: null,
        extendedMaturityDate: null,
        rateTypeId: "",
        allInRate: "",
        loanSpread: "",
        indexId: "",
        fallbackRate: "",
        lookbackDays: "",
        businessDayAdjustmentId: "",
        roundingId: "",
        indexFloor: "",
        embeddedCap: "",
        spreadReductionTerms: "", //spreadReductionTerms renamed to rate modification on frontend
        paidOffDate: null,
        paymentDay: "",
        daycountId: "",
        defaultInterestSpread: "",
        defaultInterestCondition: "",
        recurringCosts: "",
        additionalInterest: "",
        prepaymentPenalty: "",
        actualPrepaymentPenalty: "",
        balanceAtPayoff: "",
        loanProvider: [
          {
            id: 0,
            name: null,
            loanCompanyBridge: {
              loanId: null,
              companyId: null,
              lenderAllocation: null,
              isLead: false
            }
          }
        ]
      },
      amortizationDetails: {
        amortizationStartDate: "",
        amortizationTerm: "",
        amortizationType: "",
        monthlyPayment: "",
        monthlyPrincipalReduction: "",
        amortizationRate: "",
        interestOnlyPeriods: "",
        amortizationDetails: "",
        interestCalculation: "",
        amortMisc: "",
      },
      additionalAdvances: {
        completion: "",
        completionRef: "",
        maxAdvance: "",
        maxAdvanceRef: "",
        additionalAdvanceAllocations: "",
        additionalAdvanceAllocationsRef: "",
        noticeForAdvance: "",
        noticeForAdvanceRef: "",
        drawFrequency: "",
        drawFrequencyRef: "",
        drawThresholdInterval: "",
        drawThresholdIntervalRef: "",
        drawConditions: "",
        drawConditionsRef: "",
        requiredEquity: "",
        requiredEquityRef: "",
        developmentFee:"",
        budgetCostOverruns:"",
        restorationRetainage:"",
        covenants:"",
        changeOrders:"",
        misc: "",
        miscRef: ""
      },
      insurance: {
        mandatoryPrePayments: "",
        mandatoryPrePaymentsRef: "",
        restoration: "",
        restorationRef: "",
        requiredPolicies: "",
        requiredPoliciesRef: "",
      },
      extensionTerms: {
        extensionOptions: "",
        extensionOptionsRef: "",
        extensionTermMonths: "",
        extensionTermMonthsRef: "",
        extensionRemaining: "",
        extensionRemainingRef: "",
        extensionAmortization: "",
        extensionAmortizationRef: "",
        noticePeriod: "",
        noticePeriodRef: "",
        extensionFeeAmt: "",
        extensionFeeRef: "",
        extensionFeePercent: "",
        extensionFeePercentRef: "",
        qualifications: "",
        qualificationsRef: "",
        ltvReq: "",
        ltvReqRef: "",
        dscrReq: "",
        dscrReqRef: "",
        debtYieldReq: "",
        debtYieldReqRef: "",
        misc: "",
        miscRef: ""
      },
      closingCosts: {
        originationFeeAmount: "",
        originationFeePercent: "",
        brokerFeeAmount: "",
        brokerFeePercent: "",
        lenderLegalFeeAmount: "",
        borrowerLegalFeeAmount: "",
        inspectionFeeAmount: "",
        appraisalFeeAmount: "",
        rateLockFeeAmount: "",
        hedgeFeeAmount: "",
        titleAmount: "",
        otherAmount: ""
      },
      prepaymentTerms: {
        id: "",
        prepaymentTypeId: "",
        prepaymentTypeRef: "",
        exitFee: "",
        exitFeeRef: "",
        lockoutPeriod: "",
        lockoutPeriodRef: "",
        parPrepaymentPeriod: "",
        parPrepaymentPeriodRef: "",
        spreadMaintenance:"",
        spreadMaintenanceRef:"",
        noticePeriod: "",
        noticePeriodRef: "",
        penalty: "",
        penaltyRef: "",
        other: "",
        otherRef: "",
        calculationMethodology: "",
        calculationMethodologyRef: "",
        successorBorrowerDesignation: "",
        successorBorrowerDesignationRef: "",
        defeasanceCollateral: "",
        defeasanceCollateralRef: "",
        securitiesStructure: "",
        securitiesStructureRef: "",
        portfolioMaturity: "",
        defeasanceFee:"",
        defeasanceFeeRef:"",
        transferFee:"",
        transferFeeRef:"",
        portfolioMaturityRef: "",
        makeWholePeriod: "",
        makeWholePeriodRef: "",
        pvRateAssumption: "",
        pvRateAssumptionRef: "",
        yieldMaintenancePeriod: "",
        yieldMaintenancePeriodRef: "",
        reinvestmentRateAssumption: "",
        reinvestmentRateAssumptionRef: "",
        isSecuritized: false,
        securitizationMonth: "",
        securitizationYear: ""
      },
      covenants: {
        dscrFixedRate: "",
        dscrTreasurySpread: "",
        dscrFloatingRateSpread: "",
        debtYieldAsIs: "",
        debtYieldStabilized: "",
        ltvAsIs: "",
        ltvStabilized: "",
        ltc: "",
        occupancy: "",
        notice: "",
        noticeRef: "",
        conditionsRef: "",
        conditions: "",
        maximumRelease: "",
        maximumReleaseRef: "",
        allocations: "",
        allocationsRef: "",
        partialReleaseMisc: "",
        partialReleaseMiscRef: "",
        suppFixedRateAssumption: "",
        suppFixedRateAssumptionRef: "",
        leasingCovenants: "",
        leasingCovenantsRef: "",
        leasingRestrictions: "",
        leasingRestrictionsRef: "",
        dscrBeginTestingDate: "",
        dscrFrequency: "",
        debtYieldBeginTestingDate: "",
        debtYieldFrequency: "",
        ltvBeginTestingDate: "",
        ltvFrequency: "",
        ltcBeginTestingDate: "",
        ltcFrequency: "",
        occupancyBeginTestingDate: "",
        occupancyFrequency: "",
        borrowerDocsMonthlyReportingStart: "",
        borrowerDocsQuarterlyReportingStart: "",
        borrowerDocsSemiAnnuallyReportingStart: "",
        borrowerDocsAnnuallyReportingStart: "",
        guarantoDocsMonthlyReportingStart: "",
        guarantoDocsQuarterlyReportingStart: "",
        guarantoDocsSemiAnnuallyReportingStart: "",
        guarantorDocsAnnuallyReportingStart: "",
        borrowerDeliveryPeriodMonthly: "",
        borrowerDeliveryPeriodQuarterly: "",
        borrowerDeliveryPeriodSemiAnnually: "",
        borrowerDeliveryPeriodAnnually: "",
        guarantorDeliveryPeriodMonthly: "",
        guarantorDeliveryPeriodQuarterly: "",
        guarantorDeliveryPeriodSemiAnnually: "",
        guarantorDeliveryPeriodAnnually: "",
        borrowerOfficerCertificate: "",
        guarantorOfficerCertificate: "",
        borrowerSignorId: "",
        guarantorSignorId: "",
      },
      personalGuarantors: [],
      companyGuarantors: [],
      hedgeRequirements: {
        id: "",
        requiredHedgeId: "",
        requiredHedgeIdRef: "",
        notional: "",
        notionalRef: "",
        termMonths: "",
        termMonthsRef: "",
        maxCapStrike: "",
        maxCapStrikeRef: "",
        pricingRequirements: "",
        pricingRequirementsRef: "",
        ratingsRequirements: "",
        ratingsRequirementsRef: "",
        extensions: "",
        extensionsRef: "",
        collateralAssignment: "",
        collateralAssignmentRef: "",
        legalOpinion: "",
        legalOpinionRef: "",
        executionRequirements: "",
        executionRequirementsRef: "",
        misc: "",
        miscRef: ""
      },
      extensions: [],
    };
  };

  validateExtensionTerms = (values, errors) => {
    const dscrReq = _.get(values, "extensionTerms.dscrReq");
    if (isNaN(+dscrReq.slice(0, -1))) {
      errors["extensionTerms.dscrReq"] = "Invalid DSCR";
    }
  };

  validatePrepaymentTerms = (values, errors) => {
    const isSecuritized = _.get(values, "closingCosts.isSecuritized");
    const year = _.get(values, "closingCosts.securitizationYear");
    const month = _.get(values, "closingCosts.securitizationMonth");
    if (isSecuritized) {
      if (!year) {
        errors["closingCosts.securitizationYear"] = "Enter Year";
      }
      if (!month) {
        errors["closingCosts.securitizationMonth"] = "Enter Month";
      }
      if (year && month) {
        if (year > 2100 || year < 1900) {
          errors["closingCosts.securitizationYear"] = "Enter Valid Year";
        }
      }
    }
  };

  modifyDetailsValues = details => {
    details.allInRate = transformRate(details.allInRate);
    if (_.get(details, "rateTypeId") === interestRateTypes.FIXED) {
      details.interestRate = transformRate(details.interestRate);
      details.amortizationRate = "";
      details.loanSpread = transformRate(details.loanSpread);
      details.spreadReductionTerms = "";
      details.lookbackDays = "";
      details.roundingId = "";
      details.indexFloor = "";
      details.embeddedCap = "";
    } else {
      details.loanSpread = transformRate(details.loanSpread);
      details.indexFloor = transformRate(details.indexFloor);
      details.embeddedCap = transformRate(details.embeddedCap);
      details.interestRate = "";
      details.rateReductionTerms = "";
    }
  };

  modifyAmortizationDetails = amortDetails => {
    amortDetails.amortizationRate = transformRate(amortDetails.amortizationRate);
  };

  modifyPrepaymentTerms = prepaymentTerms => {
    prepaymentTerms.exitFee = transformRate(prepaymentTerms.exitFee);
  };

  modifyPersonalGuarantors = personalGuarantors => {
    if (personalGuarantors) {
      for (let i = 0; i < personalGuarantors.length; i++) {
        const guarantor = personalGuarantors[i];
        guarantor.recoursePercent = transformRate(guarantor.recoursePercent);
        guarantor.leverage = transformRate(guarantor.leverage);
        // material radio elements don't like non-string inputs
        if (guarantor.burndown === "1") {
          guarantor.burndown = true;
        } else if (guarantor.burndown === "0") {
          guarantor.burndown = false;
        } else {
          guarantor.burndown = null;
        }
      }
    }
  };

  modifyCompanyGuarantors = companyGuarantors => {
    if (companyGuarantors) {
      for (let i = 0; i < companyGuarantors.length; i++) {
        const guarantor = companyGuarantors[i];
        guarantor.contactId = null;
        guarantor.recoursePercent = transformRate(guarantor.recoursePercent);
        guarantor.leverage = transformRate(guarantor.leverage);
        // material radio elements don't like non-string inputs
        if (guarantor.burndown === "1") {
          guarantor.burndown = true;
        } else if (guarantor.burndown === "0") {
          guarantor.burndown = false;
        } else {
          guarantor.burndown = null;
        }
      }
    }
  };

  modifyExtensionTerms = extensionTerms => {
    extensionTerms.extensionOptions = transformDecimal(extensionTerms.extensionOptions);
    extensionTerms.extensionTermMonths = transformDecimal(extensionTerms.extensionTermMonths);
    extensionTerms.extensionRemaining = transformDecimal(extensionTerms.extensionRemaining);
    extensionTerms.extensionFeeAmt = transformDecimal(extensionTerms.extensionFeeAmt);
    extensionTerms.extensionFeePercent = transformRate(extensionTerms.extensionFeePercent);
    extensionTerms.dscrReq = transformDscr(extensionTerms.dscrReq);
    extensionTerms.ltvReq = transformRate(extensionTerms.ltvReq);
    extensionTerms.debtYieldReq = transformRate(extensionTerms.debtYieldReq);
  };

  modifyClosingCosts = closingCosts => {
    closingCosts.brokerFeePercent = transformRate(closingCosts.brokerFeePercent);
    closingCosts.originationFeePercent = transformRate(closingCosts.originationFeePercent);
    closingCosts.otherFeePercent = transformRate(closingCosts.otherFeePercent);

    closingCosts.originationFeeAmount = transformDecimal(closingCosts.originationFeeAmount);
    closingCosts.brokerFeeAmount = transformDecimal(closingCosts.brokerFeeAmount);
    closingCosts.lenderLegalFeeAmount = transformDecimal(closingCosts.lenderLegalFeeAmount);
    closingCosts.borrowerLegalFeeAmount = transformDecimal(closingCosts.borrowerLegalFeeAmount);
    closingCosts.inspectionFeeAmount = transformDecimal(closingCosts.inspectionFeeAmount);
    closingCosts.appraisalFeeAmount = transformDecimal(closingCosts.appraisalFeeAmount);
    closingCosts.rateLockFeeAmount = transformDecimal(closingCosts.rateLockFeeAmount);
    closingCosts.hedgeFeeAmount = transformDecimal(closingCosts.hedgeFeeAmount);
    closingCosts.titleAmount = transformDecimal(closingCosts.titleAmount);
    closingCosts.otherAmount = transformDecimal(closingCosts.otherAmount);
  };

  modifyCovenants = covenants => {
    covenants.dscrFixedRate = transformRate(covenants.dscrFixedRate);
    covenants.dscrTreasurySpread = transformRate(covenants.dscrTreasurySpread);
    covenants.dscrFloatingRateSpread = transformRate(covenants.dscrFloatingRateSpread);
    covenants.debtYieldAsIs = transformRate(covenants.debtYieldAsIs);
    covenants.debtYieldStabilized = transformRate(covenants.debtYieldStabilized);
    covenants.ltvAsIs = transformRate(covenants.ltvAsIs);
    covenants.ltvStabilized = transformRate(covenants.ltvStabilized);
    covenants.ltc = transformRate(covenants.ltc);
    covenants.occupancy = transformRate(covenants.occupancy);
    covenants.suppFixedRateAssumption = transformRate(covenants.suppFixedRateAssumption);    
  };

  modifyPrepaymentSchedules = prepaymentSchedules => {
    if (prepaymentSchedules) {
      for (let i = 0; i < prepaymentSchedules.length; i++) {
        const schedule = prepaymentSchedules[i];
        schedule.percent = transformRate(schedule.percent);
        schedule.replacementRateSpread = transformRate(schedule.replacementRateSpread);
        this.clearIrrelevantFields(schedule);
      }
    }
  };

  modifyRecourse = recourse => {
    if (recourse) {
      recourse.amount = transformRate(recourse.amount);
      this.clearIrrelevantFields(recourse);
    }
  };

  modifyHedgeRequirements = hedgeRequirements => {
    if (hedgeRequirements) {
      hedgeRequirements.maxCapStrike = transformRate(hedgeRequirements.maxCapStrike);
      this.clearIrrelevantFields(hedgeRequirements);
    }
  };
  modifyValuesForSerialization = values => {
    this.modifyDetailsValues(values.details);
    this.modifyAmortizationDetails(values.amortizationDetails);
    this.modifyExtensionTerms(values.extensionTerms);
    this.modifyClosingCosts(values.closingCosts);
    this.modifyPrepaymentTerms(values.prepaymentTerms);
    this.modifyPersonalGuarantors(values.personalGuarantors);
    this.modifyCompanyGuarantors(values.companyGuarantors);
    this.modifyCovenants(values.covenants);
    this.modifyPrepaymentSchedules(values.prepaymentSchedules);
    this.modifyRecourse(values.recourse);
    this.modifyHedgeRequirements(values.hedgeRequirements)
    return values;
  };

  clearIrrelevantFields(schedule) {
    switch (schedule.prepaymentTypeId) {
      case NONE:
        this.clearFields(schedule, ["openPeriod", "percent", "aggregateInterestMethodId", "aggregateInterestDefinedAmt", "aggregateInterestMonthsOfInterest"]);
        break;
      case LOCKOUT:
        this.clearFields(schedule, ["openPeriod", "percent", "aggregateInterestMethodId", "aggregateInterestDefinedAmt", "aggregateInterestMonthsOfInterest"]);
        break;
      case YIELD_MAINTENANCE:
      case YIELD_MAINTENANCE_AGENCY:
      case MAKE_WHOLE:
        this.clearFields(schedule, ["aggregateInterestMethodId", "aggregateInterestDefinedAmt", "aggregateInterestMonthsOfInterest"]);
        break;
      case FLAT_PERCENTAGE:
        this.clearFields(schedule, ["openPeriod", "aggregateInterestMethodId", "aggregateInterestDefinedAmt", "aggregateInterestMonthsOfInterest"]);
        break;
      case DEFEASANCE:
      case SPREAD_MAINTENANCE:
        this.clearFields(schedule, ["percent", "aggregateInterestMethodId", "aggregateInterestDefinedAmt", "aggregateInterestMonthsOfInterest"]);
        break;
      case AGGREGATE_INTEREST:
        this.clearFields(schedule, ["percent", "openPeriod"]);
        break;
      default:
        break;
    }
  }

  clearFields(entity, fields) {
    for (let i = 0; i < fields.length; i++) {
      entity[fields[i]] = null;
    }
  }

  render() {
    const { id, guestPermissions } = this.props;
    const { initialValues } = this.state;
    const loanId = _.get(this.props.loan, "details.id");
    return (
      <Formik
        ref={this.formik}
        initialValues={initialValues}
        enableReinitialize
        validateOnChange={false}
        validate={values => {
          const errors = {};
          if (!values.details.loanSeniorityId) {
            errors["details.loanSeniorityId"] = "Seniority is required";
          }

          if (!values.details.loanProvider[0].id) errors["details.loanProvider[0].id"] = "Lender is required";

          if (!values.details.loanTypeId) errors["details.loanTypeId"] = "Loan Type is required";

          this.validateExtensionTerms(values, errors);
          this.validatePrepaymentTerms(values, errors);
          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          const loanDetailsRequest = {};
          if (!id) {
            loanDetailsRequest.isNew = true;
          } else {
            loanDetailsRequest.id = id;
            loanDetailsRequest.isNew = false;
            const clonedValues = _.cloneDeep(values);
            loanDetailsRequest.loanDetails = this.modifyValuesForSerialization(clonedValues);
            loanDetailsRequest.callback = this.state.callbackToCall;
            this.props.updateLoanDetails(loanDetailsRequest);
          }
          setSubmitting(false);
        }}
        render={({ values, setFieldValue }) => (
          <>
            <SimpleConfirmDialog
              modalOpen={this.state.cashflowOverrideResetModalOpen}
              handleNo={() => this.setState({ cashflowOverrideResetModalOpen: false, cashflowOverrideModalShouldUseSaveAndReturn: false })}
              message={`Saving these changes will clear cashflow overrides for this  ${
                this.props.quoteConditions.isQuote ? "quote" : "loan"
              }. Do you wish to proceed?`}
              title="Reset Cashflow Confirmation"
              yesText="Yes, Proceed"
              noText="No"
              handleYes={() => {
                this.formik.current.state.values.resetCashflows = true;
                if (this.state.cashflowOverrideModalShouldUseSaveAndReturn) {
                  this.saveAndReturn({ ignoreCashflowOverride: true });
                } else {
                  this.save({ ignoreCashflowOverride: true });
                }
                this.setState({ cashflowOverrideResetModalOpen: false, cashflowOverrideModalShouldUseSaveAndReturn: false });
              }}
            />
            <LoanSection
              deal={this.props.deal}
              loanId={loanId}
              isEditing={this.props.isEditing}
              isQuote={this.props.quoteConditions.isQuote}
              isPassedLoan={this.props.quoteConditions.isPassedLoan}
              guestPermissions={guestPermissions}
            />
            {!this.props.quoteConditions.isPassedLoan && (
              <>
                <CovenantsSection deal={this.props.deal} isEditing={this.props.isEditing} isQuote={this.props.quoteConditions.isQuote} />
                <RecourseSection loanId={loanId} isEditing={this.props.isEditing} isQuote={this.props.quoteConditions.isQuote} setFieldValue={setFieldValue} />
                <HedgeRequirementsSection
                  loanId={loanId}
                  isEditing={this.props.isEditing}
                  isQuote={this.props.quoteConditions.isQuote}
                  ratingsTriggers={_.get(values, "ancillary.ratingsTriggers", [])}
                />
                {loanId && this.props.deal && this.props.quoteConditions.isQuote && (
                  <LinkedHedgesSection match={this.props.match} loanId={loanId} dealId={this.props.deal.id} deal={this.props.deal} />
                )}
                {!this.props.quoteConditions.isQuote && <QuoteHistorySection />}
                {this.props.quoteConditions.isQuote && (
                  <OtherSection loanId={loanId} isEditing={this.props.isEditing} isQuote={this.props.quoteConditions.isQuote} />
                )}
              </>
            )}
            {this.props.quoteConditions.isPassedLoan && (
              <PassedQuoteSection loanId={loanId} isEditing={this.props.isEditing} isQuote={this.props.quoteConditions.isQuote} />
            )}
          </>
        )}
      />
    );
  }
}

function mapStateToProps() {
  return {};
}

export default compose(
  connect(mapStateToProps, {
    updateLoanDetails
  })
)(LoanDetailsForm);
